/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI
 * (C) Copyright IBM Corp. 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import StringResources from 'nls/StringResources';
import ConfirmReplaceDialog from './ConfirmReplaceDialog';
import ItemDetail from './ItemDetail';
import ToastEnum from './ToastEnum';
import VisualItems from './VisualItems';
import SVGFileInput from '../../components/SVGFileInput';
import {
	FlexItem,
	FlexLayout,
	HSpacer,
	SearchInput,
	VSpacer,
	Button
} from '@ba/ba-ui-carbon-toolkit';
import { TableToolbar, TableBatchActions, TableBatchAction } from '@ba/ba-ui-carbon';
import uploadImg from '@ba/ba-graphics/dist/icons-js/upload_16';
import '../../../stylesheets/all.scss';

export default class ContentPanel extends Component {

	static propTypes = {
		definition: PropTypes.object.isRequired,
		svgFiles: PropTypes.object,
		selectedIndices: PropTypes.array.isRequired,
		search: PropTypes.string.isRequired,
		searchMatches: PropTypes.array,
		zipMaxSize: PropTypes.number,
		imgMaxSize: PropTypes.number,
		onChange: PropTypes.func.isRequired
	}

	constructor(props) {
		super(props);
	}

	_cloneJsonObject = ( _object ) => {
		return JSON.parse( JSON.stringify( _object ) );
	}

	_findSearchMatches = ( value ) => {
		const { definition, onChange } = this.props;
		let matchIndices = null;
		// if the value is empty - there's nothing being searched
		if (value) {
			matchIndices = [];
			definition.meta.content.forEach( ( item, j ) => {
				if ( item.caption.toLowerCase().indexOf( value.toLowerCase() ) !== -1 )
					matchIndices.push( j );
			} );
		}

		onChange( {
			search: value,
			searchMatches: matchIndices
		} );
	}

	// update the state when a property value is changed
	_updateItem = (property, value, index) => {
		const { definition, onChange } = this.props;
		var newDefinition = this._cloneJsonObject( definition );
		newDefinition.meta.content = newDefinition.meta.content.map((item, j) => {
			if (j === index) {
				item[property] = value;
			}
			return item;
		});

		onChange( {
			definition: newDefinition
		});
	}

	// add button parameters: action, applyToAll
	// contextMenu replace parameters: replaceIdx
	_updateSVGFiles = ( sourceNames, blobUrls, action, applyToAll, replaceIdx ) => {
		const { definition, onChange } = this.props;
		let newDefinition = this._cloneJsonObject( definition );
		let count = 0;
		while ( sourceNames.length > 0 ) {
			let sourceName = sourceNames[0];
			let blobUrl = blobUrls[ 0 ];
			let existIdx = -1;
			for ( var idx = 0; idx < newDefinition.meta.content.length; ++idx ) {
				if ( newDefinition.meta.content[ idx ].source === sourceName ) {
					existIdx = idx;
					break;
				}
			}

			if ( replaceIdx >= 0 && replaceIdx < newDefinition.meta.content.length ) {
				newDefinition.files[ replaceIdx ] = blobUrl;
				count = 1;
			} else if ( existIdx >= 0 ) {
				if ( action === 'confirm' )
					break;

				if ( action === 'replace' ) {
					newDefinition.files[ existIdx ] = blobUrl;
					count++;
				}
			} else {
				newDefinition.meta.content.push( {
					'key': sourceName,
					'caption': sourceName,
					'source': sourceName
				} );
				newDefinition.files.push( blobUrl );
				count++;
			}

			sourceNames.shift();
			blobUrls.shift();

			if ( !applyToAll )
				action = 'confirm';
		}

		let toastMsg = count ? { type: ToastEnum.SUCCESS } : null;
		if ( count === 1 )
			toastMsg.info = StringResources.get( 'schematicsSingleAddSuccessed' );
		else if ( count > 1 )
			toastMsg.info = StringResources.get( 'schematicsMultipleAddSuccessed', { 'count': count } );

		onChange( {
			definition: newDefinition,
			svgFiles: sourceNames.length > 0 ? { 'sourceNames': sourceNames, 'blobUrls': blobUrls } : null,
			search: '',
			searchMatches: null,
			toastMsg: toastMsg
		} );
	}

	_newSvgFiles = ( sourceNames, blobUrls, replaceIdx ) => {
		this._updateSVGFiles( sourceNames, blobUrls, 'confirm', false, replaceIdx );
	}

	_toastSchematicsAddFailed = ( _error ) => {
		this.props.onChange( {
			toastMsg: {
				type: ToastEnum.ERROR,
				info: ( _error && _error.message ) || StringResources.get('schematicsAddFailed')
			}
		} );
	}

	_toggleSelection = ( idx, multiSelect ) => {
		const { selectedIndices, onChange } = this.props;

		var newSelectedIndices = this._cloneJsonObject( selectedIndices );

		var index = newSelectedIndices.indexOf( idx );
		if ( index !== -1 && multiSelect ) {
			newSelectedIndices.splice( index, 1 );
		} else {
			if ( multiSelect )
				newSelectedIndices.push( idx );
			else
				newSelectedIndices = newSelectedIndices.find( item => item === idx ) !== undefined ? [] : [ idx ];
		}

		onChange( {
			selectedIndices: newSelectedIndices
		} );
	}

	_replaceSelected = () => {
		if ( this.replaceSvgFilesInputRef ) {
			const { selectedIndices } = this.props;
			this.replaceSvgFilesInputRef.openFileSelector( false, selectedIndices[0] );
		}
	}

	_deleteSelected = () => {
		const { selectedIndices } = this.props;
		this._handleDeletions( selectedIndices );
	}

	_handleDeletions = ( itemIdxArray ) => {
		const { definition, searchMatches, selectedIndices, onChange } = this.props;
		if ( itemIdxArray.length > 0 ) {
			let deletedCount = 0;

			let newDefinition = {
				meta: (({ ...clone }) => clone )(definition.meta),
				files: []
			};

			// Add the icon if defined
			if ( definition.iconUrl ) {
				newDefinition.iconUrl = definition.iconUrl;
			}

			newDefinition.meta.content = [];

			let newSelectedIndices = [];
			let newSearchMatches = searchMatches ? [] : null;
			for( let idx = 0; idx < definition.files.length; ++idx ) {
				if ( !itemIdxArray.includes( idx ) ) {
					newDefinition.meta.content.push( definition.meta.content[ idx ] );
					newDefinition.files.push( definition.files[ idx ] );
					let newIdx = newDefinition.files.length - 1;
					if ( selectedIndices.includes( idx ) ) {
						newSelectedIndices.push( newIdx );
					}
					if ( searchMatches && searchMatches.includes( idx ) ) {
						newSearchMatches.push( newIdx );
					}
				} else {
					deletedCount += 1;
				}
			}

			if ( deletedCount > 0 ) {
				let toastInfo = ( deletedCount === 1 ) ?
					StringResources.get('schematicsSingleDeletionSuccessed') :
					StringResources.get('schematicsMultipleDeletionSuccessed', { count: deletedCount } );

				onChange( {
					selectedIndices: newSelectedIndices,
					searchMatches: newSearchMatches,
					definition: newDefinition,
					toastMsg: {
						type: ToastEnum.SUCCESS,
						info: toastInfo
					}
				} );
			}
		}
	}

	_replaceAddSVGFiles = ( applyToAll ) => {
		let newSvgFiles = this._cloneJsonObject( this.props.svgFiles );
		this._updateSVGFiles( newSvgFiles.sourceNames, newSvgFiles.blobUrls, 'replace', applyToAll );
	}

	_cancelAddSVGFiles = ( applyToAll ) => {
		let newSvgFiles = this._cloneJsonObject( this.props.svgFiles );
		this._updateSVGFiles( newSvgFiles.sourceNames, newSvgFiles.blobUrls, 'cancel', applyToAll );
	}

	_renderHeader = ( selectedCount ) => {
		if ( selectedCount > 0 )
			return this._renderBatchActions( selectedCount );

		let searchTitle = StringResources.get('searchSchematic');
		const { search, definition, onChange, zipMaxSize, imgMaxSize } = this.props;
		return (
			<div style={ { display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexGrow: 1 } }>
				<div>
					<SearchInput
						light
						tabIndex={0}
						placeholder={searchTitle}
						className='clsSVGSearch'
						value={search}
						disabled={ definition.meta.content.length === 0 }
						onChange={this._findSearchMatches}
						onClear={ () => onChange( { search: '', searchMatches: null } ) }
					/>
				</div>
				<div>
					<SVGFileInput
						ref={(ref) => { this.addSvgFilesInputRef = ref;} }
						successCallback={this._newSvgFiles}
						failCallback={this._toastSchematicsAddFailed}
						zipMaxSize={ zipMaxSize }
						imgMaxSize={ imgMaxSize }
					/>
					<Button
						icon={uploadImg.id}
						kind='ghost'
						onClick={ () => this.addSvgFilesInputRef ? this.addSvgFilesInputRef.openFileSelector( true ) : {} }
						title={StringResources.get('addSchematic')}
					> {StringResources.get('addSchematic')} </Button>
				</div>
			</div>
		);
	}

	_renderBatchActions = ( selectedCount ) => {
		const { zipMaxSize, imgMaxSize } = this.props;
		return (
			<TableToolbar>
				<TableBatchActions
					style={ { justifyContent: 'space-between' } }
					totalSelected={ selectedCount }
					shouldShowBatchActions={ true }
					onCancel={ () => { this.props.onChange( { selectedIndices: [] } ); } } >
					{ selectedCount === 1 ?
						<div>
							<TableBatchAction
								isVisible={selectedCount === 1}
								tabIndex={selectedCount === 1 ? 0 : -1 }
								onClick={ () => { this._replaceSelected(); } }
								renderIcon={ () => null }>
								{StringResources.get('schematicsContextMenuReplace')}
							</TableBatchAction>
							<SVGFileInput
								ref={(ref) => { this.replaceSvgFilesInputRef = ref;} }
								successCallback={this._newSvgFiles}
								failCallback={this._toastSchematicsAddFailed}
								zipMaxSize={ zipMaxSize }
								imgMaxSize={ imgMaxSize }
							/>
						</div> : null
					}
					<TableBatchAction
						tabIndex={0}
						onClick={ () => { this._deleteSelected(); } }
						renderIcon={ () => null }>
						{StringResources.get('schematicsContextMenuDelete')}
					</TableBatchAction>
				</TableBatchActions>
			</TableToolbar>
		);
	}

	//
	// Main render function
	//
	render () {
		const { selectedIndices, searchMatches, definition, onChange, svgFiles } = this.props;
		// calculate the values which drive the detail panel. Have to take into account the searchMatches - which
		// reduces the set of items to see
		let filteredItems = !searchMatches ? selectedIndices : selectedIndices.filter( function( value ) {
			return searchMatches.indexOf( value ) !== -1;
		} );
		let selectedCount = filteredItems.length;
		let selectedItem = ( selectedCount === 1 ) ? definition.meta.content[filteredItems[ 0 ]] : {};
		let selectedFileRef = ( selectedCount === 1) ? definition.files[filteredItems[ 0 ]] : '';
		let selectedIndex = ( selectedCount === 1 ) ? filteredItems[ 0 ] : -1;

		return (
			<div className="clsSVGTabsStyle">
				<VSpacer size={2}/>
				{ this._renderHeader( selectedCount ) }
				<VSpacer size={2}/>
				<FlexLayout
					direction='row'
					justifyContent='space-between'
					alignItems='stretch'
					style={{ width:'100%' }}
				>
					<FlexItem
						style={{ width:'593px' }}
					>
						<VisualItems
							items={definition.meta.content}
							files={definition.files}
							selections={selectedIndices}
							searchMatches={searchMatches}
							selectVisualItemCallback={ this._toggleSelection }
							deSelectAllCallback={ () => {
								onChange( {
									selectedIndices: []
								});
							} }
						/>
					</FlexItem>
					<FlexItem>
						<HSpacer size={1}/>
					</FlexItem>
					<FlexItem
						style={{ width:'271px' }}
					>
						<ItemDetail
							item={selectedItem}
							fileRef={selectedFileRef}
							index={selectedIndex}
							selectionCount={selectedCount}
							updateItemCallback={ this._updateItem }
						/>
					</FlexItem>
				</FlexLayout>
				{ !!svgFiles && (
					<ConfirmReplaceDialog
						sourceName={svgFiles ? svgFiles.sourceNames[0] : ''}
						isApplyToAll={false}
						okCallback={this._replaceAddSVGFiles}
						cancelCallback={this._cancelAddSVGFiles}
					/> ) }
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/js/dialog/schematic/ContentPanel.js